import React, { useState } from 'react';
import styles from './Products.module.css';
import productsData from '../../data/productsData.json'; // Import the data
import { Tooltip, IconButton, Pagination } from '@mui/material';

// Import Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Define the structure of a product
interface Product {
    id: number;
    sku: string;
    name: string;
    category: string;
    brand: string | null;
    price: number;
    stock: number;
    status: string;
    dateAdded: string;
}

const Products: React.FC = () => {
    const [products, setProducts] = useState<Product[]>(productsData);
    // Add state for filters later
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [brandFilter, setBrandFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    
    // Calculate pagination
    const totalPages = Math.ceil(products.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = products.slice(startIndex, endIndex);

    // Placeholder handlers
    const handleSearch = () => {
        console.log('Searching...', { searchTerm, categoryFilter, brandFilter, statusFilter });
        // Add actual filtering logic here
    };

    const handleReset = () => {
        setSearchTerm('');
        setCategoryFilter('');
        setBrandFilter('');
        setStatusFilter('');
        console.log('Filters reset');
        // Maybe refetch or reset to original data
    };

    const handleAddProduct = () => {
        console.log('Add new product');
        // Logic to open a modal or navigate to an add form
    };
    
    const handleViewProduct = (id: number) => {
        console.log(`View product with ID: ${id}`);
        // Logic to view product details
    };

    const handleEditProduct = (id: number) => {
        console.log(`Edit product with ID: ${id}`);
        // Logic to edit product
    };

    const handleDeleteProduct = (id: number) => {
        console.log(`Delete product with ID: ${id}`);
        // Filter out the product - temporary example
        setProducts(prev => prev.filter(p => p.id !== id));
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    const getStatusBadgeClass = (status: string) => {
        if (status === 'Active') {
            return styles.active;
        } else if (status === 'Inactive') {
            return styles.inactive;
        }
        return '';
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <h2>Products</h2>
                    <p>Create and manage products</p>
                </div>
                <div className={styles.actionsSection}>
                    <span className={styles.lastSync}>Last Sync: {new Date().toLocaleString()} <RefreshIcon fontSize="small"/></span>
                    <button className={styles.actionButton}><DownloadIcon fontSize="small"/> Download Template</button>
                    <button className={styles.actionButton}><UploadIcon fontSize="small"/> Bulk Import</button>
                    <button className={`${styles.actionButton} ${styles.addButton}`} onClick={handleAddProduct}><AddIcon fontSize="small"/> Add Product</button>
                </div>
            </div>

            <div className={styles.filterArea}>
                <div className={styles.filterGroup}>
                    <label>Category</label>
                    <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                        <option value="">-- Select Category --</option>
                        {/* Populate options dynamically later */}
                        <option value="Electronics">Electronics</option>
                        <option value="Accessories">Accessories</option>
                        <option value="Photography">Photography</option>
                    </select>
                </div>
                 <div className={styles.filterGroup}>
                    <label>Brand</label>
                    <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
                        <option value="">-- Select Brand --</option>
                         {/* Populate options dynamically later */}
                        <option value="TechCorp">TechCorp</option>
                        <option value="ApexMobile">ApexMobile</option>
                        <option value="LogicGear">LogicGear</option>
                         <option value="DellView">DellView</option>
                         <option value="CanonShot">CanonShot</option>
                    </select>
                </div>
                 <div className={styles.filterGroup}>
                    <label>Status</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="">-- Select Status --</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label>Search</label>
                    <input 
                        type="text" 
                        placeholder="Product Name / SKU"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                 <div className={styles.filterActions}>
                    <button className={`${styles.actionButton} ${styles.searchButton}`} onClick={handleSearch}><SearchIcon fontSize="small"/> Search</button>
                    <button className={`${styles.actionButton} ${styles.resetButton}`} onClick={handleReset}>Reset</button>
                </div>
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>SKU</th>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Brand</th>
                            <th>Price</th>
                            <th>Stock</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentProducts.map((product) => (
                            <tr key={product.id}>
                                <td>{product.sku}</td>
                                <td>{product.name}</td>
                                <td>{product.category}</td>
                                <td>{product.brand}</td>
                                <td>${product.price.toFixed(2)}</td>
                                <td>{product.stock}</td>
                                <td>
                                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(product.status)}`}>
                                        {product.status}
                                    </span>
                                </td>
                                <td>
                                    <div className={styles.actionButtons}>
                                        <Tooltip title="View Details">
                                            <VisibilityIcon 
                                                fontSize="small"
                                                className={styles.actionIcon}
                                                onClick={() => handleViewProduct(product.id)}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Edit Product">
                                            <EditIcon 
                                                fontSize="small"
                                                className={styles.actionIcon}
                                                onClick={() => handleEditProduct(product.id)}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Delete Product">
                                            <DeleteIcon 
                                                fontSize="small"
                                                className={styles.actionIcon}
                                                onClick={() => handleDeleteProduct(product.id)}
                                            />
                                        </Tooltip>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className={styles.paginationContainer}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </div>
        </div>
    );
};

export default Products; 